import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  FactCheck as FactCheckIcon,
  RemoveCircleOutline as SkippedIcon,
} from '@mui/icons-material';
import { pollQuartrCheck, reportQuartrIssue, startQuartrCheck } from '../../api/contentCompletion';
import {
  CompanyContentHealth,
  QuartrCheckResult,
  QuartrCheckStatus,
} from '../../types/contentCompletion';
import BatchQuartrReviewDialog from './BatchQuartrReviewDialog';
import {
  getCheckResultSummary,
  getClosestMatchLabel,
  shouldReportQuartrIssue,
} from './quartrCheckUtils';

type ItemStatus =
  | 'queued'
  | 'checking'
  | 'check_ready'
  | 'check_failed'
  | 'no_changes'
  | 'saved'
  | 'error';

interface BatchItem {
  companyId: string;
  companyName: string;
  company: CompanyContentHealth;
  status: ItemStatus;
  checkResult?: QuartrCheckResult;
  error?: string;
}

interface BatchQuartrCheckPanelProps {
  companies: CompanyContentHealth[];
  onClose: () => void;
  onComplete: () => void;
  onIssueReported: () => void;
}

const CHECK_STATUS_META: Record<
  QuartrCheckStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  valid: { label: 'Valid', color: 'success' },
  no_changes: { label: 'No changes', color: 'default' },
  missing_id_found: { label: 'ID found', color: 'info' },
  ticker_mismatch: { label: 'Ticker mismatch', color: 'warning' },
  invalid_id: { label: 'Invalid ID', color: 'error' },
  lookup_failed: { label: 'Lookup failed', color: 'error' },
  missing_ticker: { label: 'Missing ticker', color: 'warning' },
  not_applicable: { label: 'Not applicable', color: 'default' },
};

const STATUS_META: Record<
  ItemStatus,
  { label: string; color: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  queued: { label: 'Queued', color: 'default' },
  checking: { label: 'Checking…', color: 'info' },
  check_ready: { label: 'Ready to review', color: 'warning' },
  check_failed: { label: 'Check failed', color: 'error' },
  no_changes: { label: 'No changes', color: 'default' },
  saved: { label: 'Saved', color: 'success' },
  error: { label: 'Error', color: 'error' },
};

const isReviewable = (result: QuartrCheckResult): boolean =>
  result.status === 'success' && result.apply_fields.length > 0;

const BatchQuartrCheckPanel: React.FC<BatchQuartrCheckPanelProps> = ({
  companies,
  onClose,
  onComplete,
  onIssueReported,
}) => {
  const [items, setItems] = useState<BatchItem[]>(() =>
    companies.map((company) => ({
      companyId: company.company_id,
      companyName: company.company_name,
      company,
      status: 'queued' as ItemStatus,
    }))
  );
  const [running, setRunning] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  const cancelledRef = useRef(false);

  const updateItem = useCallback((companyId: string, patch: Partial<BatchItem>) => {
    setItems((prev) =>
      prev.map((item) => (item.companyId === companyId ? { ...item, ...patch } : item))
    );
  }, []);

  const reportFailedCheck = useCallback(
    async (item: BatchItem, checkResult?: QuartrCheckResult, error?: string) => {
      const result = checkResult;
      if (result && !shouldReportQuartrIssue(result)) return;

      const issueType =
        result?.status === 'failure'
          ? 'check_failed'
          : result?.check_status || 'lookup_failed';

      try {
        await reportQuartrIssue({
          company_id: item.companyId,
          company_name: item.companyName,
          issue_type: issueType,
          ticker: result?.current.ticker || item.company.ticker,
          quartr_company_id: result?.current.quartr_company_id || item.company.quartr_company_id,
          summary: result?.summary || error,
          closest_match: result ? getClosestMatchLabel(result) : undefined,
          searched_queries: result?.searched_queries || [],
          note: result?.warnings?.[0] || error,
        });
        onIssueReported();
      } catch {
        /* batch continues */
      }
    },
    [onIssueReported]
  );

  useEffect(() => {
    cancelledRef.current = false;

    const run = async () => {
      for (const seed of companies) {
        if (cancelledRef.current) break;

        updateItem(seed.company_id, { status: 'checking' });

        try {
          const { run_id } = await startQuartrCheck({
            company_id: seed.company_id,
            company_name: seed.company_name,
          });

          const checkResult = await new Promise<QuartrCheckResult>((resolve) => {
            const stop = pollQuartrCheck(run_id, (update) => {
              if (update.status !== 'running') {
                stop();
                resolve(update);
              }
            });
          });

          if (cancelledRef.current) break;

          if (checkResult.status === 'failure') {
            const batchItem: BatchItem = {
              companyId: seed.company_id,
              companyName: seed.company_name,
              company: seed,
              status: 'check_failed',
            };
            await reportFailedCheck(
              batchItem,
              checkResult,
              checkResult.error || checkResult.summary || 'Quartr check failed'
            );
            updateItem(seed.company_id, {
              status: 'check_failed',
              checkResult,
              error: checkResult.error || checkResult.summary || 'Quartr check failed',
            });
            continue;
          }

          if (isReviewable(checkResult)) {
            updateItem(seed.company_id, {
              status: 'check_ready',
              checkResult,
            });
            continue;
          }

          if (shouldReportQuartrIssue(checkResult)) {
            await reportFailedCheck(
              {
                companyId: seed.company_id,
                companyName: seed.company_name,
                company: seed,
                status: 'no_changes',
              },
              checkResult
            );
          }

          updateItem(seed.company_id, {
            status: 'no_changes',
            checkResult,
          });
        } catch (err: unknown) {
          if (cancelledRef.current) break;
          const msg =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
            (err as Error)?.message ||
            'Quartr check failed';
          await reportFailedCheck(
            {
              companyId: seed.company_id,
              companyName: seed.company_name,
              company: seed,
              status: 'error',
            },
            undefined,
            msg
          );
          updateItem(seed.company_id, { status: 'check_failed', error: msg });
        }
      }

      if (!cancelledRef.current) setRunning(false);
    };

    run();

    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompanySaved = (companyId: string) => {
    updateItem(companyId, { status: 'saved' });
    onComplete();
  };

  const toReviewItem = (item: BatchItem) => ({
    companyId: item.companyId,
    companyName: item.companyName,
    company: item.company,
    checkResult: item.checkResult as QuartrCheckResult,
  });

  const reviewItems = items
    .filter((item) => item.status === 'check_ready' && item.checkResult)
    .map(toReviewItem);

  const validationItems = items
    .filter(
      (item) =>
        item.checkResult &&
        (item.status === 'no_changes' ||
          item.status === 'check_failed' ||
          item.status === 'check_ready')
    )
    .map(toReviewItem);

  const total = items.length;
  const finishedCount = items.filter(
    (item) => item.status !== 'queued' && item.status !== 'checking'
  ).length;
  const processing = items.find((item) => item.status === 'checking');
  const readyCount = items.filter((item) => item.status === 'check_ready').length;
  const validationCount = items.filter((item) => item.status === 'no_changes').length;
  const allDone =
    !running &&
    items.every(
      (item) =>
        item.status === 'saved' ||
        item.status === 'check_failed' ||
        item.status === 'no_changes' ||
        item.status === 'error' ||
        item.status === 'check_ready'
    );

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'info.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FactCheckIcon color="info" />
          <Typography variant="h6" fontWeight={700}>
            Quartr check — {total} compan{total === 1 ? 'y' : 'ies'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={onClose}>
            {allDone ? 'Done' : 'Dismiss'}
          </Button>
        </Box>

        <LinearProgress
          variant="determinate"
          value={total === 0 ? 0 : (finishedCount / total) * 100}
          sx={{ mb: 1, borderRadius: 1 }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {running ? (
            processing ? (
              <>
                Checking {finishedCount + 1}/{total}: <strong>{processing.companyName}</strong>…
              </>
            ) : (
              'Starting Quartr checks…'
            )
          ) : (
            <>
              Checks complete.
              {readyCount > 0 ? ` ${readyCount} with proposed updates to review.` : ''}
              {validationCount > 0 ? ` ${validationCount} validated with no changes.` : ''}
            </>
          )}
        </Typography>

        {readyCount > 0 && !running && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Review proposed Quartr entity names and IDs before saving to Neo4j.
          </Alert>
        )}

        {!running &&
          items.some(
            (item) =>
              item.status === 'check_failed' ||
              (item.checkResult ? shouldReportQuartrIssue(item.checkResult) : false)
          ) && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Failed or unresolved Quartr checks were automatically added to the unresolved Quartr
              lookups list.
            </Alert>
          )}

        {!running && (readyCount > 0 || validationCount > 0) && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {readyCount > 0 && (
              <Button variant="contained" color="info" onClick={() => setReviewOpen(true)}>
                Review updates ({readyCount})
              </Button>
            )}
            {validationItems.length > 0 && (
              <Button variant="outlined" color="info" onClick={() => setValidationOpen(true)}>
                View validation ({validationItems.length})
              </Button>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
          {items.map((item) => {
            const meta = STATUS_META[item.status];
            const checkMeta = item.checkResult
              ? CHECK_STATUS_META[item.checkResult.check_status]
              : null;
            const secondary =
              item.error ||
              (item.checkResult ? getCheckResultSummary(item.checkResult) : undefined);
            return (
              <ListItem key={item.companyId} alignItems="flex-start">
                <Box
                  sx={{
                    mr: 1.5,
                    mt: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    width: 24,
                    justifyContent: 'center',
                  }}
                >
                  {item.status === 'checking' && <CircularProgress size={18} />}
                  {item.status === 'saved' && <CheckCircleIcon color="success" fontSize="small" />}
                  {(item.status === 'no_changes' || item.status === 'queued') && (
                    <SkippedIcon color="disabled" fontSize="small" />
                  )}
                  {(item.status === 'check_failed' || item.status === 'error') && (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                </Box>
                <ListItemText
                  primary={item.companyName}
                  secondary={secondary}
                  secondaryTypographyProps={{
                    color: item.error ? 'error' : 'textSecondary',
                    sx: { whiteSpace: 'normal' },
                  }}
                />
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {checkMeta && (
                    <Chip
                      label={checkMeta.label}
                      color={checkMeta.color}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <Chip label={meta.label} color={meta.color} size="small" variant="outlined" />
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Paper>

      {reviewOpen && reviewItems.length > 0 && (
        <BatchQuartrReviewDialog
          open
          onClose={() => setReviewOpen(false)}
          items={reviewItems}
          onCompanySaved={handleCompanySaved}
          onComplete={onComplete}
          onIssueResolved={onIssueReported}
        />
      )}

      {validationOpen && validationItems.length > 0 && (
        <BatchQuartrReviewDialog
          open
          readOnly
          onClose={() => setValidationOpen(false)}
          items={validationItems}
          title={`Quartr validation — ${validationItems.length} companies`}
        />
      )}
    </>
  );
};

export default BatchQuartrCheckPanel;
